<template>
  <div class="customers-list-container mx-lg-4 mx-1">
    <h2 class="page-title">
      <i class="fas fa-users me-2"></i>
      Usuarios
    </h2>
    <!-- Search and filter bar -->
    <div class="search-filter-bar mb-4">
      <div class="search-bar">
        <div class="input-group">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Buscar por usuario, email o DNI..." 
            v-model="searchQuery"
          >
          <button class="btn search-btn">
            <i class="fas fa-search"></i>
          </button>
        </div>
      </div>
      <div class="filter-bar d-flex flex-wrap gap-2 mt-3">
        <select class="form-select form-select-sm filter-select" v-model="roleFilter">
          <option value="">Todos los roles</option>
          <option v-for="role in availableRoles" :key="role.id" :value="role.name">{{ role.name }}</option>
          <option value="__none__">Sin rol</option>
        </select>
        <select class="form-select form-select-sm filter-select" v-model="customerTypeFilter">
          <option value="">Tipo de cliente</option>
          <option value="REGULAR">Regular</option>
          <option value="DISTRIBUTOR">Mayorista</option>
        </select>
        <select class="form-select form-select-sm filter-select" v-model="staffFilter">
          <option value="">Todos</option>
          <option value="true">Empleados</option>
          <option value="false">Clientes</option>
        </select>
        <button 
          v-if="roleFilter || customerTypeFilter || staffFilter" 
          class="btn btn-sm clear-filters-btn"
          @click="clearFilters"
        >
          <i class="fas fa-times me-1"></i>Limpiar
        </button>
      </div>
    </div>

    <!-- Customers table for larger screens -->
    <div class="table-responsive d-none d-md-block">
      <table class="table customers-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Email</th>
            <th>Estado</th>
            <th>Última compra</th>
            <th>Total consumido</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </td>
          </tr>
          <tr v-else-if="!filteredCustomers || filteredCustomers.length === 0">
            <td colspan="6" class="text-center py-5">
              <div class="no-results">
                <i class="fas fa-users fa-2x mb-3"></i>
                <p>No se encontraron clientes</p>
              </div>
            </td>
          </tr>
          <tr v-for="customer in filteredCustomers" :key="customer.id" class="customer-row" :class="{'blocked-customer': !customer.is_active}">
            <td class="customer-name">
              <div class="d-flex align-items-center">
                <router-link :to="`/configuracion/clientes/${customer.id}`" class="d-flex align-items-center text-decoration-none">
                  <div class="avatar-placeholder" :class="{'blocked-avatar': !customer.is_active}">
                    {{ getInitials(customer.username) }}
                  </div>
                  <div class="ms-3">
                    <div class="d-flex align-items-center flex-wrap gap-y-1">
                      <div class="name">{{ customer.username }}</div>
                      <div v-if="!customer.is_active" class="ms-2 badge bg-danger">Bloqueado</div>
                      <div v-if="customer.customer_type === 'DISTRIBUTOR'" class="ms-2 badge bg-info">Mayorista</div>
                      <div v-if="customer.distributor_pending_approval" class="ms-2 badge bg-secondary">Distri. pendiente</div>
                      <template v-if="hasRbacRoles(customer)">
                        <span
                          v-for="role in customer.roles"
                          :key="role.id"
                          class="ms-2 badge bg-primary me-1 mb-1"
                        >{{ role.name }}</span>
                      </template>
                      <div
                        v-else-if="showStaffWhenNoRbac(customer)"
                        class="ms-2 badge bg-warning text-dark"
                      >Staff</div>
                      <div
                        v-else-if="showSinRolBadge(customer)"
                        class="ms-2 badge bg-secondary"
                      >Sin rol</div>
                    </div>
                  </div>
                </router-link>
              </div>
            </td>
            <td class="customer-email">
              <div class="d-flex align-items-center">
                <span>{{ customer.email }}</span>
                <i 
                  v-if="customer.admin_notes" 
                  class="fas fa-comment-alt ms-2 text-info notes-icon"
                  :title="customer.admin_notes"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                ></i>
              </div>
            </td>
            <td class="customer-status text-center">
              <span 
                v-if="customer.is_active" 
                class="badge bg-success status-badge clickable"
                @click="confirmCancelCustomer(customer)"
                title="Click para bloquear cliente"
              >
                <i class="fas fa-check-circle me-1"></i>
                Activo
              </span>
              <span 
                v-else 
                class="badge bg-danger status-badge clickable"
                @click="confirmUnblockCustomer(customer)"
                title="Click para desbloquear cliente"
              >
                <i class="fas fa-ban me-1"></i>
                Bloqueado
              </span>
            </td>
            <td class="last-purchase">
              <div v-if="customer.last_purchase" class="fs-7">
                {{ formatDate(customer.last_purchase) }}
                <div class="small text-muted">{{ formatTimeAgo(customer.last_purchase) }}</div>
              </div>
              <div v-else class="text-muted">Sin compras</div>
            </td>
            <td class="total-spent">
              <div class="amount text-white fw-light">${{ formatCurrency(customer.total_spent) }}</div>
              <div class="small text-muted">
                <a href="#" @click.prevent="viewCustomerOrders(customer)" class="text-primary">
                  {{ customer.total_orders || 0 }} compras
                </a>
              </div>
            </td>
            <td class="actions">
              <div class="simple-dropdown">
                <button 
                  class="btn btn-icon" 
                  @click.stop="toggleDropdown(customer.id)"
                  :data-customer-id="customer.id"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div 
                  class="simple-dropdown-menu" 
                  v-show="activeDropdown === customer.id"
                  :data-dropdown-id="customer.id"
                >
                  <router-link 
                    :to="`/configuracion/clientes/${customer.id}`"
                    class="simple-dropdown-item"
                  >
                    <i class="fas fa-user"></i> Ver perfil
                  </router-link>
                  <a 
                    href="#" 
                    class="simple-dropdown-item"
                    @click.prevent="viewCustomerOrders(customer)"
                  >
                    <i class="fas fa-shopping-cart"></i> Ver compras
                  </a>
                  <a
                    href="#"
                    class="simple-dropdown-item"
                    @click.prevent="openAssignRoleModal(customer)"
                  >
                    <i class="fas fa-user-tag"></i> Asignar rol
                  </a>
                  <a
                    href="#"
                    class="simple-dropdown-item"
                    @click.prevent="toggleDistributor(customer)"
                  >
                    <i class="fas fa-truck"></i>
                    {{ customer.customer_type === 'DISTRIBUTOR' ? 'Quitar Mayorista' : 'Marcar Mayorista' }}
                  </a>
                  <a 
                    v-if="customer.is_active" 
                    href="#" 
                    class="simple-dropdown-item text-danger"
                    @click.prevent="confirmCancelCustomer(customer)"
                  >
                    <i class="fas fa-ban"></i> Bloquear cliente
                  </a>
                  <a 
                    v-else 
                    href="#" 
                    class="simple-dropdown-item text-brand"
                    @click.prevent="confirmUnblockCustomer(customer)"
                  >
                    <i class="fas fa-unlock"></i> Desbloquear cliente
                  </a>
                  <hr class="dropdown-divider my-1">
                  <a 
                    href="#" 
                    class="simple-dropdown-item text-danger"
                    @click.prevent="confirmDeleteCustomer(customer)"
                  >
                    <i class="fas fa-trash"></i> Eliminar cliente
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Card view for mobile screens -->
    <div class="customer-cards d-md-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <div v-else-if="!filteredCustomers || filteredCustomers.length === 0" class="text-center py-5">
        <div class="no-results">
          <i class="fas fa-users fa-2x mb-3"></i>
          <p>No se encontraron clientes</p>
        </div>
      </div>
      <div v-else class="customer-card-list">
        <div v-for="customer in filteredCustomers" :key="customer.id" 
             class="customer-card mb-3" 
             :class="{'blocked-customer-card': !customer.is_active}">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-center">
                <div class="avatar-placeholder me-2" :class="{'blocked-avatar': !customer.is_active}">
                  {{ getInitials(customer.username) }}
                </div>
                <div>
                  <div class="d-flex align-items-center flex-wrap">
                    <div class="name">{{ customer.username }}</div>
                    <div v-if="!customer.is_active" class="ms-2 badge bg-danger clickable" @click="confirmUnblockCustomer(customer)">Bloqueado</div>
                    <div v-if="customer.customer_type === 'DISTRIBUTOR'" class="ms-2 badge bg-info">Mayorista</div>
                    <div v-if="customer.distributor_pending_approval" class="ms-2 badge bg-secondary">Distri. pendiente</div>
                    <template v-if="hasRbacRoles(customer)">
                      <span
                        v-for="role in customer.roles"
                        :key="role.id"
                        class="ms-2 badge bg-primary me-1 mb-1"
                      >{{ role.name }}</span>
                    </template>
                    <div
                      v-else-if="showStaffWhenNoRbac(customer)"
                      class="ms-2 badge bg-warning text-dark"
                    >Staff</div>
                    <div
                      v-else-if="showSinRolBadge(customer)"
                      class="ms-2 badge bg-secondary"
                    >Sin rol</div>
                  </div>
                  <div class="email small d-flex align-items-center">
                    <span>{{ customer.email }}</span>
                    <i 
                      v-if="customer.admin_notes" 
                      class="fas fa-comment-alt ms-2 text-info notes-icon"
                      :title="customer.admin_notes"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                    ></i>
                  </div>
                </div>
              </div>
              <div class="simple-dropdown">
                <button class="btn btn-icon" @click.stop="toggleDropdown(customer.id)" :data-customer-id="customer.id">
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="simple-dropdown-menu" v-show="activeDropdown === customer.id" :data-dropdown-id="customer.id">
                  <router-link :to="`/configuracion/clientes/${customer.id}`" class="simple-dropdown-item">
                    <i class="fas fa-user"></i> Ver perfil
                  </router-link>
                  <a href="#" class="simple-dropdown-item" @click.prevent="viewCustomerOrders(customer)">
                    <i class="fas fa-shopping-cart"></i> Ver compras
                  </a>
                  <a href="#" class="simple-dropdown-item" @click.prevent="openAssignRoleModal(customer)">
                    <i class="fas fa-user-tag"></i> Asignar rol
                  </a>
                  <a href="#" class="simple-dropdown-item" @click.prevent="toggleDistributor(customer)">
                    <i class="fas fa-truck"></i>
                    {{ customer.customer_type === 'DISTRIBUTOR' ? 'Quitar Mayorista' : 'Marcar Mayorista' }}
                  </a>
                  <a v-if="customer.is_active" href="#" class="simple-dropdown-item text-danger" @click.prevent="confirmCancelCustomer(customer)">
                    <i class="fas fa-ban"></i> Bloquear cliente
                  </a>
                  <a v-else href="#" class="simple-dropdown-item text-brand" @click.prevent="confirmUnblockCustomer(customer)">
                    <i class="fas fa-unlock"></i> Desbloquear cliente
                  </a>
                  <hr class="dropdown-divider my-1">
                  <a href="#" class="simple-dropdown-item text-danger" @click.prevent="confirmDeleteCustomer(customer)">
                    <i class="fas fa-trash"></i> Eliminar cliente
                  </a>
                </div>
              </div>
            </div>
            
            <div class="customer-info-grid">
              <div class="info-item">
                <div class="info-label">Estado</div>
                <div class="info-value">
                  <span 
                    v-if="customer.is_active" 
                    class="badge bg-success status-badge clickable"
                    @click="confirmCancelCustomer(customer)"
                    title="Click para bloquear cliente"
                  >
                    <i class="fas fa-check-circle me-1"></i>
                    Activo
                  </span>
                  <span 
                    v-else 
                    class="badge bg-danger status-badge clickable"
                    @click="confirmUnblockCustomer(customer)"
                    title="Click para desbloquear cliente"
                  >
                    <i class="fas fa-ban me-1"></i>
                    Bloqueado
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Última compra</div>
                <div class="info-value">
                  <div v-if="customer.last_purchase">
                    {{ formatDate(customer.last_purchase) }}
                    <div class="small text-muted">{{ formatTimeAgo(customer.last_purchase) }}</div>
                  </div>
                  <div v-else class="text-muted">Sin compras</div>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Total consumido</div>
                <div class="info-value text-white fw-light">${{ formatCurrency(customer.total_spent) }}</div>
                <div class="small text-muted">
                  <a href="#" @click.prevent="viewCustomerOrders(customer)" class="text-primary">
                    {{ customer.total_orders || 0 }} compras
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredCustomers && filteredCustomers.length > 0" class="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-4">
      <div class="showing-info text-muted">
        Mostrando {{ filteredCustomers.length }} de {{ totalCustomers }} usuarios | Página {{ currentPage }} de {{ totalPages }}
      </div>
      <nav aria-label="Customer pagination">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="currentPage--">
              <i class="fas fa-chevron-left"></i>
            </a>
          </li>
          
          <!-- Primera página siempre visible -->
          <li class="page-item" :class="{ active: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="currentPage = 1">1</a>
          </li>
          
          <!-- Mostrar puntos suspensivos si hay muchas páginas antes de la actual -->
          <li v-if="currentPage > 4" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Mostrar páginas cercanas a la página actual -->
          <li v-for="page in displayedPageNumbers" :key="page" class="page-item" :class="{ active: page === currentPage }">
            <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
          </li>
          
          <!-- Mostrar puntos suspensivos si hay muchas páginas después de la actual -->
          <li v-if="currentPage < totalPages - 3" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Última página siempre visible si hay más de una página -->
          <li v-if="totalPages > 1" class="page-item" :class="{ active: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="currentPage = totalPages">{{ totalPages }}</a>
          </li>
          
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="currentPage++">
              <i class="fas fa-chevron-right"></i>
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Contact Modal -->
    <div v-if="showContactModal" class="modal-overlay">
      <div class="contact-modal">
        <div class="modal-header">
          <h5>Contactar a {{ selectedCustomer.name }}</h5>
          <button type="button" class="btn-close" @click="showContactModal = false"></button>
        </div>
        <div class="modal-body">
          <div class="form-group mb-3">
            <label class="form-label">Asunto</label>
            <input type="text" class="form-control" v-model="contactForm.subject" placeholder="Asunto del mensaje">
          </div>
          <div class="form-group mb-3">
            <label class="form-label">Mensaje</label>
            <textarea class="form-control" v-model="contactForm.message" rows="4" placeholder="Escriba su mensaje aquí..."></textarea>
          </div>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showContactModal = false">
            Cancelar
          </button>
          <button class="btn btn-primary w-100" @click="sendMessage" :disabled="isSending">
            <i class="fas fa-spinner fa-spin me-2" v-if="isSending"></i>
            {{ isSending ? 'Enviando...' : 'Enviar Mensaje' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Customer Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay">
      <div class="customer-details-modal">
        <div class="modal-header">
          <h5>
            Detalles del Cliente
            <span v-if="selectedCustomer.is_blocked" class="ms-2 badge bg-danger">Bloqueado</span>
          </h5>
          <button type="button" class="btn-close" @click="showDetailsModal = false"></button>
        </div>
        <div class="modal-body p-0">
          <!-- Customer Summary -->
          <div class="customer-profile p-3">
            <div class="d-flex flex-column flex-md-row align-items-md-center mb-3">
              <div class="avatar-placeholder customer-avatar mb-3 mb-md-0">
                {{ getInitials(selectedCustomer.name) }}
              </div>
              <div class="ms-md-3">
                <h4 class="customer-name mb-1">{{ selectedCustomer.name }}</h4>
                <div class="customer-email mb-1">
                  <i class="fas fa-envelope me-2"></i>
                  {{ selectedCustomer.email || 'No disponible' }}
                </div>
                <div class="customer-dni">
                  <i class="fas fa-id-card me-2"></i>
                  {{ selectedCustomer.dni || 'DNI no registrado' }}
                </div>
              </div>
            </div>
            
            <div class="customer-stats">
              <div class="row row-cols-1 row-cols-md-3 g-3">
                <div class="col">
                  <div class="stat-card">
                    <div class="stat-label">Total gastado</div>
                    <div class="stat-value">${{ formatCurrency(selectedCustomer.total_spent || 0) }}</div>
                  </div>
                </div>
                <div class="col">
                  <div class="stat-card">
                    <div class="stat-label">Compras</div>
                    <div class="stat-value">{{ selectedCustomer.total_orders || 0 }}</div>
                  </div>
                </div>
                <div class="col">
                  <div class="stat-card">
                    <div class="stat-label">Última compra</div>
                    <div class="stat-value">{{ selectedCustomer.last_purchase ? formatDate(selectedCustomer.last_purchase) : 'N/A' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Purchase History -->
          <div class="purchase-history p-3">
            <h5 class="section-title">Historial de compras</h5>
            
            <div v-if="isLoadingPurchases" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <div class="mt-2">Cargando historial de compras...</div>
            </div>
            <div v-else-if="customerPurchases.length === 0" class="no-purchases text-center py-4">
              <i class="fas fa-shopping-cart fa-2x mb-2"></i>
              <p>No hay compras registradas para este cliente</p>
            </div>
            <div v-else class="purchase-list">
              <div v-for="purchase in customerPurchases" :key="purchase.id" class="purchase-item">
                <div class="purchase-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                  <div class="purchase-date mb-2 mb-md-0">
                    <i class="far fa-calendar me-2"></i>
                    {{ formatDate(purchase.date) }}
                    <span class="ms-2 small text-muted">{{ formatTimeAgo(purchase.date) }}</span>
                  </div>
                  <div class="purchase-amount fw-bold">${{ formatCurrency(purchase.total) }}</div>
                </div>
                <div class="purchase-details mt-2">
                  <div class="table-responsive">
                    <table class="table purchase-items-table mb-0">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th class="text-center">Cant.</th>
                          <th class="text-end">Precio</th>
                          <th class="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in purchase.items" :key="`${purchase.id}-${item.id}`">
                          <td class="item-name">{{ item.name }}</td>
                          <td class="text-center">{{ item.quantity }}</td>
                          <td class="text-end">${{ formatCurrency(item.price) }}</td>
                          <td class="text-end">${{ formatCurrency(item.quantity * item.price) }}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="3" class="text-end">Subtotal:</td>
                          <td class="text-end">${{ formatCurrency(purchase.subtotal) }}</td>
                        </tr>
                        <tr v-if="purchase.discount">
                          <td colspan="3" class="text-end">Descuento:</td>
                          <td class="text-end text-danger">-${{ formatCurrency(purchase.discount) }}</td>
                        </tr>
                        <tr v-if="purchase.tax">
                          <td colspan="3" class="text-end">Impuesto:</td>
                          <td class="text-end">${{ formatCurrency(purchase.tax) }}</td>
                        </tr>
                        <tr>
                          <td colspan="3" class="text-end fw-bold">Total:</td>
                          <td class="text-end fw-bold">${{ formatCurrency(purchase.total) }}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showDetailsModal = false">
            Cerrar
          </button>
          <button class="btn btn-primary w-100" @click="contactCustomer(selectedCustomer)">
            <i class="fas fa-envelope me-1"></i>
            Contactar Cliente
          </button>
        </div>
      </div>
    </div>

    <!-- Add confirmation modal for cancel/block action -->
    <div v-if="showCancelModal" class="modal-overlay">
      <div class="cancel-modal">
        <div class="modal-header">
          <h5 class="text-danger"><i class="fas fa-exclamation-triangle me-2"></i>Bloquear Cliente</h5>
          <button type="button" class="btn-close" @click="showCancelModal = false"></button>
        </div>
        <div class="modal-body">
          <p>¿Está seguro de que desea bloquear a <strong>{{ selectedCustomer?.name || selectedCustomer?.username }}</strong>?</p>
          <p class="text-muted">Esta acción impedirá que el cliente realice nuevas compras. Se puede revertir más tarde.</p>
          
          <div class="mt-3">
            <label for="adminNotes" class="form-label text-white">Nota del administrador (opcional)</label>
            <input 
              type="text" 
              id="adminNotes"
              class="form-control" 
              v-model="adminNotes"
              maxlength="40"
              placeholder="Ej: Conducta inadecuada, reembolsos frecuentes..."
            >
            <div class="form-text text-muted">
              Máximo 40 caracteres. Esta nota será visible para otros administradores.
            </div>
          </div>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showCancelModal = false">
            Cancelar
          </button>
          <button class="btn btn-danger w-100" @click="cancelCustomer" :disabled="isCancelling">
            <i class="fas fa-spinner fa-spin me-2" v-if="isCancelling"></i>
            {{ isCancelling ? 'Procesando...' : 'Confirmar Bloqueo' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add unblock confirmation modal -->
    <div v-if="showUnblockModal" class="modal-overlay">
      <div class="cancel-modal">
        <div class="modal-header">
          <h5 class="text-brand"><i class="fas fa-unlock me-2"></i>Desbloquear Cliente</h5>
          <button type="button" class="btn-close" @click="showUnblockModal = false"></button>
        </div>
        <div class="modal-body">
          <p>¿Está seguro de que desea desbloquear a <strong>{{ selectedCustomer?.name || selectedCustomer?.username }}</strong>?</p>
          <p class="text-muted">Esta acción permitirá que el cliente vuelva a realizar compras en la tienda.</p>
          
          <div class="mt-3">
            <label for="adminNotesUnblock" class="form-label text-white">Nota del administrador (opcional)</label>
            <input 
              type="text" 
              id="adminNotesUnblock"
              class="form-control" 
              v-model="adminNotes"
              maxlength="40"
              placeholder="Ej: Problema resuelto, cuenta verificada..."
            >
            <div class="form-text text-muted">
              Máximo 40 caracteres. Esta nota será visible para otros administradores.
            </div>
          </div>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showUnblockModal = false">
            Cancelar
          </button>
          <button class="btn btn-brand w-100" @click="unblockCustomerConfirm" :disabled="isProcessing">
            <i class="fas fa-spinner fa-spin me-2" v-if="isProcessing"></i>
            {{ isProcessing ? 'Procesando...' : 'Confirmar Desbloqueo' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="cancel-modal">
        <div class="modal-header">
          <h5 class="text-danger"><i class="fas fa-trash me-2"></i>Eliminar Cliente</h5>
          <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle me-2"></i>
            <strong>¡Atención!</strong> Esta acción no se puede deshacer.
          </div>
          <p>¿Está seguro de que desea eliminar permanentemente al cliente <strong>{{ selectedCustomer?.username || selectedCustomer?.name }}</strong>?</p>
          <p class="text-muted">Se eliminarán todos los datos asociados con este cliente, incluyendo su historial de compras.</p>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showDeleteModal = false">
            Cancelar
          </button>
          <button class="btn btn-danger w-100" @click="deleteCustomer" :disabled="isDeleting">
            <i class="fas fa-spinner fa-spin me-2" v-if="isDeleting"></i>
            {{ isDeleting ? 'Eliminando...' : 'Confirmar Eliminación' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Assign Role Modal -->
    <div v-if="showAssignRoleModal" class="modal-overlay">
      <div class="cancel-modal">
        <div class="modal-header">
          <h5><i class="fas fa-user-tag me-2"></i>Asignar Roles</h5>
          <button type="button" class="btn-close" @click="showAssignRoleModal = false"></button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-3">
            Cliente: <strong class="text-white">{{ selectedCustomer?.username }}</strong>
          </p>
          <div v-if="loadingRoles" class="text-center py-3">
            <span class="spinner-border spinner-border-sm me-2"></span> Cargando roles...
          </div>
          <div v-else-if="availableRoles.length === 0" class="text-muted">
            No hay roles disponibles. <router-link to="/configuracion/roles" class="text-primary">Crear roles</router-link>
          </div>
          <div v-else class="roles-assign-grid">
            <div
              v-for="role in availableRoles"
              :key="role.id"
              class="role-assign-item"
              :class="{ selected: selectedRoles.includes(role.name) }"
              @click="toggleAssignRole(role.name)"
            >
              <i class="fas fa-user-tag me-2"></i>
              <span>{{ role.name }}</span>
              <i v-if="selectedRoles.includes(role.name)" class="fas fa-check ms-auto"></i>
            </div>
          </div>
        </div>
        <div class="modal-footer flex-column flex-md-row">
          <button class="btn btn-outline-secondary w-100 mb-2 mb-md-0 me-md-2" @click="showAssignRoleModal = false">
            Cancelar
          </button>
          <button class="btn btn-primary w-100" @click="confirmAssignRoles" :disabled="isSavingRoles">
            <i class="fas fa-spinner fa-spin me-2" v-if="isSavingRoles"></i>
            {{ isSavingRoles ? 'Guardando...' : 'Guardar Roles' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, onUnmounted, computed } from 'vue'
import { useCustomers } from '@/composables/useCustomers'
import { useToast } from 'vue-toastification'
import { useWindowSize } from '@/composables/useWindowSize'
import { getRoles } from '@/services/rbac'
import { assignCustomerRoles, setCustomerType } from '@/services/customers'

const {
  loading,
  searchQuery,
  currentPage,
  totalPages,
  totalCustomers,
  filteredCustomers,
  fetchCustomers,
  viewCustomerOrders,
  handleBlockCustomer,
  handleUnblockCustomer,
  handleDeleteCustomer,
  roleFilter,
  customerTypeFilter,
  staffFilter
} = useCustomers()

// Toast para notificaciones
const toast = useToast()

// Window size para adaptación responsive
const { width } = useWindowSize()
const isMobile = computed(() => width.value < 768)

// Roles for filter dropdown
const availableRoles = ref([])
const loadingRoles = ref(false)

const clearFilters = () => {
  roleFilter.value = ''
  customerTypeFilter.value = ''
  staffFilter.value = ''
}

/** Evita doble badge: is_staff + rol RBAC, o Staff + "Sin rol", o Mayorista + "Sin rol". */
const hasRbacRoles = (c) => Array.isArray(c.roles) && c.roles.length > 0
const showStaffWhenNoRbac = (c) => !hasRbacRoles(c) && Boolean(c.is_staff)
const showSinRolBadge = (c) =>
  !hasRbacRoles(c) &&
  !c.is_staff &&
  c.customer_type !== 'DISTRIBUTOR' &&
  !c.distributor_pending_approval

// Variables para modales
const showContactModal = ref(false)
const showDetailsModal = ref(false)
const showCancelModal = ref(false)
const showUnblockModal = ref(false)
const showDeleteModal = ref(false)
const selectedCustomer = ref(null)
const isCancelling = ref(false)
const isProcessing = ref(false)
const isDeleting = ref(false)
const isSending = ref(false)
const contactForm = ref({ subject: '', message: '' })
const customerPurchases = ref([])
const isLoadingPurchases = ref(false)
const adminNotes = ref('')

// Role assignment
const showAssignRoleModal = ref(false)
const selectedRoles = ref([])
const isSavingRoles = ref(false)

const openAssignRoleModal = async (customer) => {
  selectedCustomer.value = customer
  selectedRoles.value = customer.roles ? customer.roles.map(r => r.name) : []
  showAssignRoleModal.value = true
  activeDropdown.value = null
  if (availableRoles.value.length === 0) {
    loadingRoles.value = true
    try {
      availableRoles.value = await getRoles()
    } catch {
      // ignore
    } finally {
      loadingRoles.value = false
    }
  }
}

const toggleAssignRole = (roleName) => {
  const idx = selectedRoles.value.indexOf(roleName)
  if (idx === -1) selectedRoles.value.push(roleName)
  else selectedRoles.value.splice(idx, 1)
}

const confirmAssignRoles = async () => {
  if (!selectedCustomer.value) return
  isSavingRoles.value = true
  try {
    await assignCustomerRoles(selectedCustomer.value.id, selectedRoles.value)
    toast.success(`Roles actualizados para ${selectedCustomer.value.username}`)
    showAssignRoleModal.value = false
  } catch (error) {
    toast.error('Error al asignar roles')
  } finally {
    isSavingRoles.value = false
  }
}

const toggleDistributor = async (customer) => {
  const newType = customer.customer_type === 'DISTRIBUTOR' ? 'REGULAR' : 'DISTRIBUTOR'
  activeDropdown.value = null
  try {
    await setCustomerType(customer.id, newType)
    customer.customer_type = newType
    toast.success(newType === 'DISTRIBUTOR' ? `${customer.username} marcado como mayorista` : `${customer.username} marcado como regular`)
  } catch (error) {
    toast.error(error.message || 'Error al cambiar tipo de cliente')
  }
}

// Propiedad computada para mostrar solo un número limitado de páginas
const displayedPageNumbers = computed(() => {
  if (totalPages.value <= 7) {
    // Si hay pocas páginas, mostrar todas (excepto primera y última que se muestran siempre)
    return totalPages.value > 2 
      ? Array.from({ length: totalPages.value - 2 }, (_, i) => i + 2) 
      : []
  }
  
  // Determinar el rango de páginas a mostrar cerca de la página actual
  let startPage = Math.max(2, currentPage.value - 1)
  let endPage = Math.min(totalPages.value - 1, currentPage.value + 1)
  
  // Ajustar para mostrar siempre 3 páginas (si es posible)
  if (endPage - startPage < 2 && totalPages.value > 4) {
    if (currentPage.value < totalPages.value / 2) {
      endPage = Math.min(totalPages.value - 1, startPage + 2)
    } else {
      startPage = Math.max(2, endPage - 2)
    }
  }
  
  return Array.from({ length: endPage - startPage + 1 }, (_, i) => i + startPage)
})

// Dropdown functionality
const activeDropdown = ref(null)

const toggleDropdown = (customerId) => {
  if (activeDropdown.value === customerId) {
    activeDropdown.value = null
    removeDropdownOverlay()
    return
  }

  activeDropdown.value = customerId

  if (isMobile.value) {
    // Móvil: bottom sheet posicionado por CSS, solo necesitamos el overlay
    addDropdownOverlay()
  } else {
    // Desktop: posicionamiento inteligente con rAF para esperar que el DOM pinte
    requestAnimationFrame(() => {
      const button = document.querySelector(`button[data-customer-id="${customerId}"]`)
      const menu = document.querySelector(`div[data-dropdown-id="${customerId}"]`)
      if (!button || !menu) return

      const rect = button.getBoundingClientRect()
      const menuHeight = menu.offsetHeight
      const menuWidth = menu.offsetWidth
      const vh = window.innerHeight
      const vw = window.innerWidth
      const spaceBelow = vh - rect.bottom
      const spaceAbove = rect.top

      // Vertical: abrir hacia arriba si no hay espacio abajo
      if (spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow) {
        menu.style.top = `${Math.max(4, rect.top - menuHeight - 4)}px`
      } else {
        menu.style.top = `${rect.bottom + 4}px`
      }
      menu.style.bottom = 'auto'

      // Horizontal: alinear borde derecho, clampeado al viewport
      const leftPos = Math.min(rect.right - menuWidth, vw - menuWidth - 8)
      menu.style.left = `${Math.max(8, leftPos)}px`
      menu.style.right = 'auto'
    })
  }
}

const addDropdownOverlay = () => {
  removeDropdownOverlay()
  const overlay = document.createElement('div')
  overlay.className = 'dropdown-mobile-overlay'
  overlay.addEventListener('click', () => {
    activeDropdown.value = null
    removeDropdownOverlay()
  })
  document.body.appendChild(overlay)
  document.body.style.overflow = 'hidden'
}

const removeDropdownOverlay = () => {
  document.querySelectorAll('.dropdown-mobile-overlay').forEach(el => el.remove())
  document.body.style.overflow = ''
}

// Close dropdown on scroll or click outside
const handleScroll = () => {
  if (activeDropdown.value) {
    activeDropdown.value = null
    removeDropdownOverlay()
  }
}

const handleClickOutside = (e) => {
  if (!e.target.closest('.simple-dropdown') && !e.target.closest('.dropdown-mobile-overlay')) {
    activeDropdown.value = null
    removeDropdownOverlay()
  }
}

onMounted(async () => {
  window.addEventListener('scroll', handleScroll, true)
  document.addEventListener('click', handleClickOutside)
  
  fetchCustomers()
  
  // Cargar roles para el filtro
  try {
    availableRoles.value = await getRoles()
  } catch {
    // ignore
  }
  
  // Inicializar tooltips
  initializeTooltips()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true)
  document.removeEventListener('click', handleClickOutside)
  removeDropdownOverlay()
})

// Helper para inicializar tooltips
const initializeTooltips = () => {
  nextTick(() => {
    // Inicializar tooltips de Bootstrap si están disponibles
    if (typeof window !== 'undefined' && window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        new window.bootstrap.Tooltip(tooltipTriggerEl)
      })
    }
  })
}

// Helpers
const getInitials = (name) => {
  if (!name) return ''
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const formatTimeAgo = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays < 1) return 'Hoy'
  if (diffDays === 1) return 'Ayer'
  if (diffDays < 7) return `Hace ${diffDays} días`
  if (diffDays < 30) return `Hace ${Math.ceil(diffDays / 7)} semanas`
  if (diffDays < 365) return `Hace ${Math.ceil(diffDays / 30)} meses`
  return `Hace ${Math.ceil(diffDays / 365)} años`
}

const formatCurrency = (value) => {
  if (!value) return '0.00'
  return value.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const sendMessage = async () => {
  if (!contactForm.value.subject || !contactForm.value.message) {
    toast.error('Por favor complete todos los campos')
    return
  }
  
  isSending.value = true
  try {
    // Aquí iría la lógica para enviar el mensaje
    await new Promise(resolve => setTimeout(resolve, 1000)) // Simular envío
    toast.success('Mensaje enviado correctamente')
    showContactModal.value = false
    contactForm.value = { subject: '', message: '' }
  } catch (error) {
    toast.error('Error al enviar el mensaje')
  } finally {
    isSending.value = false
  }
}

const contactCustomer = (customer) => {
  selectedCustomer.value = customer
  showContactModal.value = true
  showDetailsModal.value = false
}

const cancelCustomer = async () => {
  if (!selectedCustomer.value) return
  
  isCancelling.value = true
  try {
    await handleBlockCustomer(selectedCustomer.value, adminNotes.value)
    showCancelModal.value = false
    adminNotes.value = ''
    toast.success(`Cliente ${selectedCustomer.value.name || selectedCustomer.value.username} bloqueado correctamente`)
  } catch (error) {
    toast.error('Error al bloquear el cliente')
  } finally {
    isCancelling.value = false
  }
}

const confirmCancelCustomer = (customer) => {
  selectedCustomer.value = customer
  adminNotes.value = customer.admin_notes || ''
  showCancelModal.value = true
}

const unblockCustomerConfirm = async () => {
  if (!selectedCustomer.value) return
  
  isProcessing.value = true
  try {
    await handleUnblockCustomer(selectedCustomer.value, adminNotes.value)
    showUnblockModal.value = false
    adminNotes.value = ''
    toast.success(`Cliente ${selectedCustomer.value.name || selectedCustomer.value.username} desbloqueado correctamente`)
  } catch (error) {
    toast.error('Error al desbloquear el cliente')
  } finally {
    isProcessing.value = false
  }
}

const confirmUnblockCustomer = (customer) => {
  selectedCustomer.value = customer
  adminNotes.value = customer.admin_notes || ''
  showUnblockModal.value = true
}

const deleteCustomer = async () => {
  if (!selectedCustomer.value) return
  
  isDeleting.value = true
  try {
    await handleDeleteCustomer(selectedCustomer.value)
    showDeleteModal.value = false
    selectedCustomer.value = null
  } catch (error) {
    console.error('Error al eliminar cliente:', error)
  } finally {
    isDeleting.value = false
  }
}

const confirmDeleteCustomer = (customer) => {
  selectedCustomer.value = customer
  showDeleteModal.value = true
}

// El watcher para searchQuery se maneja ya en el composable useCustomers
// No necesitamos un watcher adicional aquí para evitar peticiones duplicadas

// Función para cargar compras de un cliente al abrir el modal de detalles
const loadCustomerPurchases = async (customer) => {
  if (!customer) return
  
  isLoadingPurchases.value = true
  try {
    // Aquí se haría la llamada a la API para cargar las compras del cliente
    // Por ahora simulamos una carga
    await new Promise(resolve => setTimeout(resolve, 1000))
    customerPurchases.value = [] // Aquí se asignarían las compras reales
  } catch (error) {
    toast.error('Error al cargar las compras del cliente')
  } finally {
    isLoadingPurchases.value = false
  }
}

// Mejorar la visualización de detalles del cliente
watch(() => showDetailsModal.value, (newValue) => {
  if (newValue && selectedCustomer.value) {
    loadCustomerPurchases(selectedCustomer.value)
  }
})

// Reinicializar tooltips cuando cambien los clientes
watch(filteredCustomers, () => {
  initializeTooltips()
}, { flush: 'post' })
</script>

<style scoped>
/* ===== Container ===== */
.customers-list-container {
  background: rgba(0,0,0,0.15);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  padding: 1.5rem;
  color: #aaa;
}

/* ===== Page Title ===== */
.page-title {
  color: #ccc;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  letter-spacing: -0.01em;
}
.page-title i { color: #555; }

/* ===== Search & Filters ===== */
.search-filter-bar { max-width: 100%; }
.search-bar { max-width: 100%; }
@media (min-width: 768px) { .search-bar { max-width: 480px; } }

.search-bar .form-control {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  padding: 0.6rem 0.85rem;
  border-radius: 8px 0 0 8px;
  transition: all 0.15s ease;
}
.search-bar .form-control::placeholder { color: #444; }
.search-bar .form-control:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

.search-btn {
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #fff;
  border-radius: 0 8px 8px 0;
  padding: 0.6rem 0.85rem;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}
.search-btn:hover { background: var(--primary-dark); border-color: var(--primary-dark); color: #fff; }

.filter-select {
  width: auto;
  min-width: 130px;
  max-width: 200px;
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  border-radius: 8px;
  font-size: 0.82rem;
  padding: 0.35rem 0.65rem;
  transition: all 0.15s ease;
  flex: 1 1 auto;
}
.filter-select:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}
.filter-select option { background: #1a1a1a; color: #ccc; }

.clear-filters-btn {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  border-radius: 8px;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}
.clear-filters-btn:hover { background: rgba(255,255,255,0.05); color: #ccc; border-color: rgba(255,255,255,0.15); }

/* ===== Table (Desktop) ===== */
.customers-table {
  color: #aaa;
  border-collapse: separate;
  border-spacing: 0 4px;
}

.customers-table thead th {
  background: transparent;
  border: none;
  color: #666;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.65rem 0.75rem;
  cursor: pointer;
  transition: color 0.15s ease;
}
.customers-table thead th:hover { color: var(--primary-color); }

.customers-table tbody tr {
  background: rgba(0,0,0,0.2);
  transition: all 0.15s ease;
}
.customers-table tbody tr:hover { background: rgba(0,0,0,0.28); }

.customers-table td {
  padding: 0.75rem;
  border: none;
  vertical-align: middle;
  font-size: 0.84rem;
}
.customers-table td:first-child { border-radius: 8px 0 0 8px; }
.customers-table td:last-child  { border-radius: 0 8px 8px 0; }

/* ===== Mobile Card View ===== */
.customer-card-list { display: flex; flex-direction: column; gap: 0.5rem; }

.customer-card {
  background: rgba(0,0,0,0.2);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 0.15s ease;
}
.customer-card:hover { border-color: rgba(255,255,255,0.1); }

.blocked-customer-card {
  opacity: 0.8;
  background: rgba(220,53,69,0.04);
  border: 1px solid rgba(220,53,69,0.15);
}

.customer-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
  border-top: 1px solid rgba(255,255,255,0.04);
  padding-top: 0.75rem;
}

.info-item { display: flex; flex-direction: column; gap: 0.2rem; }

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #555;
}

.info-value { font-size: 0.84rem; color: #aaa; }

/* ===== Avatar ===== */
.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.avatar-placeholder:hover { box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.25); }

.blocked-avatar { background: #444 !important; color: rgba(255,255,255,0.6) !important; }

/* ===== Name / Email ===== */
.customer-name .d-flex.align-items-center { gap: 0.75rem; }

.name {
  font-weight: 600;
  font-size: 0.87rem;
  color: #ccc;
  transition: color 0.15s ease;
}
.router-link-exact-active .name,
.router-link-exact-active:hover .name { color: var(--primary-color); }

.email {
  font-size: 0.78rem;
  color: #555;
  transition: color 0.15s ease;
}
.router-link-exact-active .email { color: #888; }

/* ===== Badges ===== */
.badge.bg-danger { background: rgba(248,113,113,0.15) !important; color: #f87171; font-size: 0.65rem; padding: 0.2rem 0.5rem; font-weight: 600; }
.badge.bg-success { background: rgba(var(--success-color-rgb), 0.15) !important; color: var(--success-color); font-weight: 600; }
.badge.bg-primary { background: rgba(var(--primary-color-rgb), 0.2) !important; color: var(--primary-color); font-weight: 600; }
.badge.bg-info { background: rgba(96,165,250,0.15) !important; color: #60a5fa; font-weight: 600; }
.badge.bg-warning { background: rgba(251,191,36,0.15) !important; color: #fbbf24 !important; font-weight: 600; }

.status-badge.clickable {
  cursor: pointer;
  transition: all 0.15s ease;
}
.status-badge.clickable:hover { opacity: 0.85; }
.status-badge.clickable.bg-success:hover { background: rgba(var(--success-color-rgb), 0.25) !important; }
.status-badge.clickable.bg-danger:hover { background: rgba(248,113,113,0.25) !important; }

.customer-status { width: 120px; }
.customer-status .badge { font-size: 0.72rem; padding: 0.3rem 0.55rem; }

/* ===== Amount ===== */
.amount { font-weight: 600; color: var(--primary-color); }
.total-spent .small a { color: var(--primary-color); text-decoration: none; }
.total-spent .small a:hover { text-decoration: underline; }

/* ===== Blocked Row ===== */
.blocked-customer {
  opacity: 0.75;
  background: rgba(220,53,69,0.04);
}
.blocked-customer::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid rgba(248,113,113,0.15);
  border-radius: 8px;
  pointer-events: none;
}

/* ===== No Results / Loading ===== */
.no-results { text-align: center; color: #555; padding: 2rem 0; }
.no-results i { color: #444; }
.no-results p { color: #888; font-size: 0.84rem; }

.spinner-border.text-primary { color: var(--primary-color) !important; }

/* ===== Pagination ===== */
.pagination { display: flex; gap: 4px; }

.page-link {
  background: rgba(0,0,0,0.2);
  color: #888;
  border: 1px solid rgba(255,255,255,0.08);
  padding: 0.4rem 0.7rem;
  border-radius: 6px;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}
.page-link:hover { background: rgba(255,255,255,0.05); color: #ccc; border-color: rgba(255,255,255,0.12); }

.page-item.active .page-link {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-color: rgba(var(--primary-color-rgb), 0.3);
  color: var(--primary-color);
}
.page-item.disabled .page-link {
  background: rgba(0,0,0,0.1);
  border-color: rgba(255,255,255,0.04);
  color: #333;
}

.showing-info { font-size: 0.78rem; color: #555; }

@media (max-width: 576px) {
  .pagination { flex-wrap: wrap; justify-content: center; }
  .showing-info { width: 100%; text-align: center; margin-bottom: 0.75rem; }
}

/* ===== Dropdown ===== */
.simple-dropdown { position: relative; display: inline-block; }

.simple-dropdown-menu {
  position: fixed;
  z-index: 99999;
  min-width: 190px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 6px 0;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
}

/* Mobile: bottom sheet */
@media (max-width: 767px) {
  .simple-dropdown-menu {
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    top: auto !important;
    transform: none !important;
    width: 100% !important;
    max-width: none !important;
    min-width: unset;
    border-radius: 16px 16px 0 0;
    padding: 0.5rem 0.4rem 2rem;
    border: none;
    border-top: 1px solid rgba(255,255,255,0.08);
    box-shadow: 0 -4px 32px rgba(0,0,0,0.6);
    max-height: 80vh;
    overflow-y: auto;
    animation: slideUp 0.22s cubic-bezier(0.32, 0.72, 0, 1);
  }

  .simple-dropdown-menu::before {
    content: '';
    display: block;
    width: 40px;
    height: 4px;
    background: rgba(255,255,255,0.15);
    border-radius: 2px;
    margin: 0.6rem auto 0.85rem;
  }

  .simple-dropdown-menu::-webkit-scrollbar { width: 4px; }
  .simple-dropdown-menu::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.12);
    border-radius: 4px;
  }
}

/* Mobile overlay */
.dropdown-mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 99998;
  animation: fadeInOverlay 0.15s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}

@keyframes fadeInOverlay {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.simple-dropdown button { position: relative; z-index: 1; }

.btn-icon {
  color: #555;
  background: transparent;
  border: none;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-icon:hover { background: rgba(255,255,255,0.05); color: #aaa; cursor: pointer; }

.simple-dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.55rem 0.85rem;
  color: #aaa;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}
.simple-dropdown-item:hover { background: rgba(255,255,255,0.04); color: #ccc; }

.simple-dropdown-item i { width: 18px; margin-right: 8px; font-size: 0.78rem; }

.simple-dropdown-item.text-danger { color: #f87171; }
.simple-dropdown-item.text-danger:hover { background: rgba(248,113,113,0.08); }
.simple-dropdown-item.text-brand { color: var(--primary-color); }
.simple-dropdown-item.text-brand:hover { background: rgba(var(--primary-color-rgb), 0.08); }

@media (max-width: 767px) {
  .simple-dropdown-item {
    padding: 0.9rem 1.1rem;
    font-size: 0.92rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .simple-dropdown-item:last-child { border-bottom: none; }
  .simple-dropdown-item i { width: 22px; margin-right: 10px; font-size: 0.85rem; }
}

.dropdown-divider { border-top: 1px solid rgba(255,255,255,0.06); margin: 0.2rem 0; }

/* ===== Notes Icon ===== */
.notes-icon { font-size: 0.82rem; cursor: help; color: #60a5fa; transition: color 0.15s ease; }
.notes-icon:hover { color: #93bbfc !important; }
[data-bs-toggle="tooltip"] { cursor: help; }

/* ===== Modals ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1rem;
}

.contact-modal,
.cancel-modal {
  width: 100%;
  max-width: 500px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: hidden;
  animation: modal-appear 0.2s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.modal-header h5 {
  color: #ccc;
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
}

.modal-body { padding: 1.25rem; }
.modal-body p { color: #aaa; font-size: 0.84rem; }
.modal-body .text-muted { color: #555 !important; font-size: 0.82rem; }

.modal-footer {
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid rgba(255,255,255,0.06);
}

.btn-close { filter: invert(0.5); }
.btn-close:hover { filter: invert(0.8); }

@keyframes modal-appear {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Modal Buttons */
.modal-footer .btn-outline-secondary,
.modal-footer .btn-outline-secondary:hover {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  border-radius: 8px;
  font-size: 0.84rem;
  transition: all 0.15s ease;
}
.modal-footer .btn-outline-secondary:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.modal-footer .btn-primary {
  background: var(--primary-color);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-size: 0.84rem;
  transition: all 0.15s ease;
}
.modal-footer .btn-primary:hover { background: var(--primary-dark); }

.modal-footer .btn-danger {
  background: rgba(248,113,113,0.2);
  border: 1px solid rgba(248,113,113,0.3);
  color: #f87171;
  border-radius: 8px;
  font-size: 0.84rem;
  transition: all 0.15s ease;
}
.modal-footer .btn-danger:hover { background: rgba(248,113,113,0.3); }
.modal-footer .btn-danger:disabled { opacity: 0.5; }

.modal-footer .btn-brand {
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #fff;
  border-radius: 8px;
  font-size: 0.84rem;
  transition: all 0.15s ease;
}
.modal-footer .btn-brand:hover { background: var(--primary-dark); }
.modal-footer .btn-brand:disabled { opacity: 0.5; }

/* ===== Forms ===== */
.form-control, .form-select {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  border-radius: 8px;
  font-size: 0.84rem;
  transition: all 0.15s ease;
}
.form-control::placeholder { color: #444; }
.form-control:focus, .form-select:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

.form-label { color: #888; font-size: 0.82rem; margin-bottom: 0.35rem; }
.form-text { font-size: 0.75rem; }

/* ===== Alert in Modals ===== */
.alert-warning {
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.15);
  color: #fbbf24;
  border-radius: 8px;
  font-size: 0.84rem;
}

/* ===== Customer Details Modal ===== */
.customer-details-modal {
  width: 100%;
  max-width: 800px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: hidden;
  animation: modal-appear 0.2s ease-out;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.customer-avatar {
  width: 54px;
  height: 54px;
  font-size: 1.25rem;
  margin: 0 auto;
}
@media (min-width: 768px) { .customer-avatar { margin: 0; } }

.customer-profile .customer-name { color: #ccc; font-weight: 600; font-size: 1.05rem; }
.customer-email { color: #888; font-size: 0.84rem; }
.customer-dni { color: #666; font-size: 0.84rem; }

.customer-stats { margin-top: 1.25rem; }

.stat-card {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 8px;
  padding: 0.85rem;
  text-align: center;
  height: 100%;
}
.stat-label { color: #555; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 0.35rem; }
.stat-value { font-size: 1.1rem; font-weight: 600; color: var(--primary-color); }

/* ===== Purchase History ===== */
.section-title {
  color: #ccc;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.85rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.purchase-item {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 8px;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
}

.purchase-header { padding-bottom: 0.6rem; border-bottom: 1px solid rgba(255,255,255,0.04); }
.purchase-date { color: #aaa; font-size: 0.84rem; }
.purchase-amount { color: var(--primary-color); font-weight: 600; font-size: 0.9rem; }

.purchase-items-table { color: #aaa; font-size: 0.82rem; margin-top: 0.5rem; }
.purchase-items-table th { color: #555; font-weight: 600; font-size: 0.72rem; text-transform: uppercase; border-bottom: 1px solid rgba(255,255,255,0.06); }
.purchase-items-table td { border-color: rgba(255,255,255,0.03); padding: 0.4rem 0.5rem; }
.purchase-items-table tfoot td { border-top: 1px solid rgba(255,255,255,0.06); color: #888; }

@media (max-width: 576px) {
  .purchase-items-table { font-size: 0.75rem; }
  .purchase-items-table td, .purchase-items-table th { padding: 0.35rem 0.25rem; }
}

.no-purchases { color: #555; }
.no-purchases i { opacity: 0.4; }

/* ===== Role Assignment ===== */
.roles-assign-grid { display: flex; flex-direction: column; gap: 0.35rem; }

.role-assign-item {
  display: flex;
  align-items: center;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.06);
  color: #aaa;
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.role-assign-item:hover { border-color: rgba(var(--primary-color-rgb), 0.3); background: rgba(var(--primary-color-rgb), 0.08); }
.role-assign-item.selected {
  background: rgba(var(--primary-color-rgb), 0.15);
  border-color: rgba(var(--primary-color-rgb), 0.35);
  color: var(--primary-color);
}

/* ===== Responsive ===== */
@media (max-width: 767.98px) {
  .customers-list-container { padding: 0.75rem; }
  .page-title { font-size: 1.1rem; }

  .search-filter-bar { max-width: 100%; }

  .filter-select {
    min-width: 0;
    max-width: 100%;
    width: 100%;
    flex: 1 1 calc(50% - 0.5rem);
  }

  .filter-bar {
    width: 100%;
  }

  .customers-table td,
  .customers-table th {
    padding: 0.55rem 0.5rem;
    font-size: 0.78rem;
  }

  .customer-status { width: auto; }
}

@media (max-width: 576px) {
  .customers-list-container { padding: 0.5rem; }
  .page-title { font-size: 1rem; margin-bottom: 1rem; }
  
  .filter-select {
    flex: 1 1 100%;
  }

  .customer-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pagination { flex-wrap: wrap; justify-content: center; gap: 3px; }
  .page-link { padding: 0.35rem 0.55rem; font-size: 0.75rem; }
}
</style>
